/**
 * Where a dataset's bytes come from — a zip, or a dropped folder.
 *
 * The user's own words were "after giving my folder". Dropping a directory is
 * at least as natural as zipping it first, and every browser that supports the
 * drop gives the same thing: a flat `File[]` where each file remembers its path
 * inside the folder. So the rest of the import does not need to care which it
 * was, and this is the seam that hides it: one object with `entries()`,
 * `readBlob`, `readBytes` and `readText`.
 *
 * The path a browser reports differs by how the files arrived:
 *   drag-and-drop  -> `file.path` (react-dropzone's file-selector adds it)
 *   <input webkitdirectory> -> `file.webkitRelativePath`
 *   plain multi-select      -> neither, so the bare name is the path
 * All three are normalised here to a leading-slash-free relative path.
 */

/**
 * Normalise whatever the UI hands over into a plain `File[]`.
 *
 * **This exists because its absence killed the entire attach path.** The hook's
 * entry point takes a LIST (a dropped folder is many files), but both call sites
 * — the composer's picker and the drop zone — pass a SINGLE `File`. A `File` has
 * no `length` and no `Symbol.iterator`, so `Array.from(file)` is `[]`, the
 * `if (!list.length) return;` guard fired, and attaching a zip did nothing at
 * all: no spinner, no error, no tray. Silently, every time.
 *
 * So the seam normalises instead of trusting its callers: a single File, a
 * FileList, an array or null all come back as an array.
 */
export const toFileList = (input) => {
  if (!input) return [];
  // A FileList and an Array both have a numeric length; a File does not.
  const listLike =
    typeof input.length === 'number' || typeof input[Symbol.iterator] === 'function';
  return (listLike ? Array.from(input) : [input]).filter(Boolean);
};

/** The path this file should be known by inside the dataset. */
export const pathOf = (file) => {
  const raw =
    (typeof file.path === 'string' && file.path) ||
    (typeof file.webkitRelativePath === 'string' && file.webkitRelativePath) ||
    file.name ||
    '';
  return String(raw).replace(/^\.?\//, '');
};

/** Read a Blob as bytes without needing a DOM. */
const bytesOfBlob = async (blob) => {
  if (typeof blob.arrayBuffer === 'function') return new Uint8Array(await blob.arrayBuffer());
  // Older Safari: FileReader is the only route.
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(new Uint8Array(reader.result));
    reader.onerror = () => reject(new Error('Could not read the file.'));
    reader.readAsArrayBuffer(blob);
  });
};

const textOfBlob = async (blob) => {
  if (typeof blob.text === 'function') return blob.text();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(new Error('Could not read the file.'));
    reader.readAsText(blob);
  });
};

/** A source backed by a loaded JSZip archive. */
export const fromZip = (zip) => {
  const entryOf = (path) => {
    const entry = zip && zip.file(path);
    if (!entry) throw new Error(`Missing from the archive: ${path}`);
    return entry;
  };
  return {
    kind: 'zip',
    entries: () => {
      const list = [];
      zip.forEach((path, entry) => {
        if (entry.dir) return;
        list.push({ path, size: (entry._data && entry._data.uncompressedSize) || 0 });
      });
      return list;
    },
    readBlob: (path) => entryOf(path).async('blob'),
    readBytes: (path) => entryOf(path).async('uint8array'),
    readText: (path) => entryOf(path).async('string'),
  };
};

/** A source backed by files the user dropped or picked directly. */
export const fromFiles = (files = []) => {
  const byPath = new Map();
  files.forEach((file) => {
    const path = pathOf(file);
    if (path) byPath.set(path, file);
  });
  const fileOf = (path) => {
    const file = byPath.get(path);
    if (!file) throw new Error(`Missing from the selection: ${path}`);
    return file;
  };
  return {
    kind: 'files',
    entries: () => [...byPath.entries()].map(([path, file]) => ({ path, size: file.size || 0 })),
    readBlob: async (path) => fileOf(path),
    readBytes: async (path) => bytesOfBlob(fileOf(path)),
    readText: async (path) => textOfBlob(fileOf(path)),
  };
};

/**
 * The name to call this dataset.
 *
 * For a folder that is the common top-level directory, which is what the user
 * thinks they dropped — not the name of whichever file happened to be first.
 */
export const nameOfSelection = (files = []) => {
  const paths = files.map(pathOf).filter(Boolean);
  if (!paths.length) return 'dataset';
  const roots = new Set(paths.map((path) => (path.includes('/') ? path.split('/')[0] : '')));
  if (roots.size === 1) {
    const [only] = [...roots];
    if (only) return only;
  }
  return 'dataset';
};
